import { useState, useEffect, useCallback, useRef } from 'react';
import { useLazyQuery, useMutation, useSubscription } from '@apollo/client/react';
import type { NotificationItem, NotificationFilterTab } from './NotificationDropdown.types';
import { NOTIFICATION_ADDED_SUBSCRIPTION } from '../../../../hooks/useLiveNotifications';
import { 
  GET_NOTIFICATIONS_QUERY, 
  MARK_NOTIFICATION_AS_READ_MUTATION, 
  MARK_ALL_NOTIFICATIONS_AS_READ_MUTATION 
} from '../../../../graphql/client';

export const useNotificationDropdown = (
  isOpen: boolean,
  onUnreadCountChange?: (count: number) => void
) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [activeTab, setActiveTab] = useState<NotificationFilterTab>('all');
  const [page, setPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<NotificationItem | null>(null);

  const listRef = useRef<HTMLDivElement | null>(null);

  const [fetchNotifications, { loading }] = useLazyQuery(GET_NOTIFICATIONS_QUERY, {
    fetchPolicy: 'network-only',
  });

  const [markReadMutation] = useMutation(MARK_NOTIFICATION_AS_READ_MUTATION);
  const [markAllReadMutation] = useMutation(MARK_ALL_NOTIFICATIONS_AS_READ_MUTATION);

  // Initial load or refresh when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setPage(1);
      fetchNotifications({
        variables: { first: 10, page: 1 },
      }).then((res: any) => {
        const data = res?.data?.notifications;
        if (data) {
          setNotifications(data.data || []);
          setHasMorePages(data.paginatorInfo?.hasMorePages ?? false);
        }
      }).catch(() => {});
    }
  }, [isOpen, fetchNotifications]);

  // Update unread count callback for topbar badge
  const unreadCount = notifications.filter(n => !n.read).length;
  useEffect(() => {
    if (onUnreadCountChange) {
      onUnreadCountChange(unreadCount);
    }
  }, [unreadCount, onUnreadCountChange]);

  // WebSocket Subscription for Real-time Notification Added
  useSubscription(NOTIFICATION_ADDED_SUBSCRIPTION, {
    onData: ({ data }) => {
      const incoming = (data as any)?.data?.notificationAdded || (data as any)?.notificationAdded;
      if (incoming) {
        setNotifications((prev) => {
          if (prev.some((item) => item.id === incoming.id)) return prev;
          return [{ ...incoming, read: false }, ...prev];
        });
      }
    },
  });

  // Infinite Scroll Handler
  const handleScroll = useCallback(() => {
    if (!listRef.current || fetchingMore || !hasMorePages) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 30) {
      setFetchingMore(true);
      const nextPage = page + 1;
      fetchNotifications({
        variables: { first: 10, page: nextPage },
      }).then((res: any) => {
        const data = res?.data?.notifications;
        if (data) {
          setNotifications((prev) => {
            const existingIds = new Set(prev.map(n => n.id));
            const newItems = (data.data || []).filter((item: NotificationItem) => !existingIds.has(item.id));
            return [...prev, ...newItems];
          });
          setPage(nextPage);
          setHasMorePages(data.paginatorInfo?.hasMorePages ?? false);
        }
      }).finally(() => {
        setFetchingMore(false);
      });
    }
  }, [listRef, fetchingMore, hasMorePages, page, fetchNotifications]);

  // Mark single notification as read
  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((item) => (item.id === id ? { ...item, read: true } : item))
    );
    markReadMutation({ variables: { id } }).catch(() => {});
  }, [markReadMutation]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
    markAllReadMutation().catch(() => {});
  }, [markAllReadMutation]);

  // Filtered list based on active tab
  const filteredNotifications = notifications.filter((item) => {
    if (activeTab === 'unread') return !item.read;
    return true;
  });

  return {
    notifications: filteredNotifications,
    rawCount: notifications.length,
    unreadCount,
    activeTab,
    setActiveTab,
    loading,
    fetchingMore,
    listRef,
    handleScroll,
    markAsRead,
    markAllAsRead,
    selectedNotification,
    setSelectedNotification,
  };
};
